// React and Libraries
import { TableColumn } from "react-data-table-component";
import { Badges, Btn } from "../../AbstractElements";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ToggleLeft, ToggleRight } from "react-feather";
import { CustomCellInterFace } from "../../Types/TablesType";
import Swal from "sweetalert2";

// Modals
import UpdateProvinceModal from "../../Components/UiKits/Modal/MasterData/Province/UpdateProvince";

// Types
import { ViewProvince } from "../../Types/MasterData/Province";

// Services
import {
  VIEW_PROVINCE_QUERY_KEY,
  provinceService,
} from "../../Services/provinceService";

const ActionCell = ({ row }: { row: ViewProvince }) => {
  const queryClient = useQueryClient();

  const handleStatusToggle = () => {
    Swal.fire({
      icon: "question",
      title: `${
        row.status_label === "ACTIVE" ? "Deactivate" : "Activate"
      } Record?`,
      html:
        row.status_label === "ACTIVE"
          ? "Are you sure you want to <b>deactivate</b> this record? Once deactivated, this record will no longer be available for selection throughout the HRIS."
          : "Are you sure you want to <b>activate</b> this record? Once activated, this record will be available for selection throughout the HRIS.",
      showCancelButton: true,
      confirmButtonText: `<i class="fa fa-${
        row.status_label === "ACTIVE" ? "times" : "check"
      }"></i> ${row.status_label === "ACTIVE" ? "Deactivate" : "Activate"}`,
      cancelButtonText: '<i class="fa fa-times"></i> Cancel',
      confirmButtonColor: row.status_label === "ACTIVE" ? "#dc3545" : "#28a745",
      cancelButtonColor: "#dc3545",

      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: `${
            row.status_label === "ACTIVE" ? "Deactivating" : "Activating"
          } Record...`,
          text: "Please wait while the record status is being updated.",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
        toggleMutation.mutate(row.id);
      }
    });
  };

  const toggleMutation = useMutation({
    mutationFn: provinceService.toggleStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VIEW_PROVINCE_QUERY_KEY });
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "success",
        title: "Transaction Successful",
        text: `Record has been ${
          row.status_label === "ACTIVE" ? "deactivated" : "activated"
        } successfully.`,
        confirmButtonColor: "#4e73df",
      });
    },
    onError: (error: any) => {
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Transaction Failed",
        text:
          error.response?.data?.message ||
          "Failed to update the record status. Please reload the page and try again.",
        confirmButtonColor: "#4e73df",
      });
    },
  });

  return (
    <div className="d-flex align-items-center gap-2">
      {/* Update Modal */}
      <UpdateProvinceModal id={row.id} />

      {/* Status Toggle Button */}
      <Btn
        size="sm"
        color={row.status_label === "ACTIVE" ? "danger" : "success"}
        className="px-2 d-inline-flex align-items-center justify-content-center"
        style={{ width: "105px" }}
        onClick={handleStatusToggle}
      >
        {row.status_label === "ACTIVE" ? (
          <ToggleLeft size={12} className="me-1" />
        ) : (
          <ToggleRight size={12} className="me-1" />
        )}

        {row.status_label === "ACTIVE" ? "Deactivate" : "Activate"}
      </Btn>
    </div>
  );
};

const CustomBadge: React.FC<CustomCellInterFace> = ({ label, color }) => (
  <Badges style={{ width: "105px" }} color={color}>
    {label}
  </Badges>
);

export const ProvinceTableColumn: TableColumn<ViewProvince>[] = [
  {
    name: "CODE",
    selector: (row) => row.id.toString().padStart(6, "0"),
    sortable: true,
    center: false,
    width: "100px",
  },
  {
    name: "NAME",
    selector: (row) => row.name,
    sortable: true,
    center: false,
  },
  {
    name: "COUNTRY",
    selector: (row) => row.country_name,
    sortable: true,
    center: false,
  },
  {
    name: "STATUS",
    selector: (row) => row.status_label,
    cell: (row) => (
      <>
        <CustomBadge
          color={row.status_label === "ACTIVE" ? "success" : "danger"}
          label={row.status_label}
        />
      </>
    ),
    sortable: true,
    width: "200px",
    center: true,
  },
  {
    name: "DATE CREATED",
    selector: (row) => row.created_date,
    cell: (row) => (
      <div style={{ whiteSpace: "pre-line" }}>
        {row.created_by_user}
        {"\n"}
        {row.created_date}
      </div>
    ),
    sortable: true,
    width: "250px",
    center: false,
  },
  {
    name: "DATE MODIFIED",
    selector: (row) => row.updated_date,
    cell: (row) => (
      <div style={{ whiteSpace: "pre-line" }}>
        {row.updated_by_user}
        {"\n"}
        {row.updated_date}
      </div>
    ),
    sortable: true,
    width: "250px",
    center: false,
  },
  {
    name: "ACTION",
    sortable: false,
    center: false,
    width: "250px",
    cell: (row) => <ActionCell row={row} />,
  },
];
